// VNEC v9 — pages/fpa.js
// FP&A Dashboard (simplified for v9)
// V9 RPC get_fpa_dashboard(p_ma_du_an) trả:
//   { year, tongHD, tongCP, tongDoanhThu, budget, variance, variance_pct, cpi, capNhat }
// Monthly trend / aging / BvA detail không có trong RPC v9 → query client-side
// từ chi_phi + ngan_sach để dựng bảng BvA.
import { supabase, getFPADashboard } from '../lib/supabase.js';
import { formatVND } from '../lib/forms.js';
import { fmtCompact, PALETTE, renderChart, destroyAllCharts } from '../lib/dashboard.js';

let _state = { selectedDA: '' };

export function renderFPA() {
  window.__pageInit = initFPA;
  return `
    <div class="dash-wrap">
      <div class="page-hd">
        <h2>FP&amp;A Dashboard</h2>
        <div class="dash-filter">
          <select id="da-filter" class="inp"><option value="">Tất cả dự án</option></select>
          <button class="btn btn-ghost btn-sm" id="dash-refresh">⟳ Refresh</button>
          <span class="muted" id="dash-updated"></span>
        </div>
      </div>
      <div id="dash-content"><div class="loading">Đang tải…</div></div>
    </div>`;
}

async function initFPA() {
  await loadDuAn();
  document.getElementById('dash-refresh').addEventListener('click', load);
  document.getElementById('da-filter').addEventListener('change', e => { _state.selectedDA = e.target.value; load(); });
  await load();
}

async function loadDuAn() {
  const { data } = await supabase.from('du_an')
    .select('ma_da, ten_da').eq('trang_thai', 'active').is('deleted_at', null).order('ma_da');
  const sel = document.getElementById('da-filter');
  (data || []).forEach(d => {
    const opt = document.createElement('option');
    opt.value = d.ma_da;
    opt.textContent = `${d.ma_da} — ${d.ten_da}`;
    sel.appendChild(opt);
  });
}

async function load() {
  destroyAllCharts();
  const content = document.getElementById('dash-content');
  content.innerHTML = '<div class="loading">Đang tải…</div>';
  try {
    const d = await getFPADashboard(_state.selectedDA || null);
    // Client-side query BvA: ngan_sach vs chi_phi aggregate by loai_cp
    const bva = await loadBvA(_state.selectedDA);
    document.getElementById('dash-updated').textContent = 'Cập nhật: ' + (d.capNhat || new Date().toLocaleString('vi-VN'));
    renderDash(d, bva);
  } catch (e) {
    content.innerHTML = `<div class="error-box">Lỗi: ${e.message}</div>`;
  }
}

async function loadBvA(maDA) {
  // ngan_sach aggregated by loai_cp
  let nsQ = supabase.from('ngan_sach').select('loai_cp, gia_tri').is('deleted_at', null);
  let cpQ = supabase.from('chi_phi').select('loai_cp, tong_tt').eq('trang_thai', 'approved').is('deleted_at', null);
  if (maDA) {
    // Resolve du_an_id from ma_da first
    const { data: da } = await supabase.from('du_an').select('id').eq('ma_da', maDA).maybeSingle();
    if (da) { nsQ = nsQ.eq('du_an_id', da.id); cpQ = cpQ.eq('du_an_id', da.id); }
  }
  const [nsRes, cpRes] = await Promise.all([nsQ, cpQ]);
  const budgetByLoai = {};
  (nsRes.data || []).forEach(r => {
    budgetByLoai[r.loai_cp] = (budgetByLoai[r.loai_cp] || 0) + Number(r.gia_tri || 0);
  });
  const actualByLoai = {};
  (cpRes.data || []).forEach(r => {
    actualByLoai[r.loai_cp] = (actualByLoai[r.loai_cp] || 0) + Number(r.tong_tt || 0);
  });
  const allKeys = new Set([...Object.keys(budgetByLoai), ...Object.keys(actualByLoai)]);
  const rows = [...allKeys].map(k => {
    const kh = budgetByLoai[k] || 0;
    const tt = actualByLoai[k] || 0;
    const chenh = kh - tt;
    const pct = kh > 0 ? Math.round(tt / kh * 100) : 0;
    const status = pct >= 110 ? 'over' : pct >= 80 ? 'warn' : 'ok';
    return { loai_cp: k, kh, tt, chenh, pct, status };
  }).sort((a, b) => b.tt - a.tt);
  const totalKH = rows.reduce((s, r) => s + r.kh, 0);
  const totalTT = rows.reduce((s, r) => s + r.tt, 0);
  return { rows, totalKH, totalTT };
}

function renderDash(d, bva) {
  const tongHD = Number(d.tongHD || 0);
  const tongCP = Number(d.tongCP || 0);
  const tongDT = Number(d.tongDoanhThu || 0);
  const budget = Number(d.budget || 0);
  const variance = Number(d.variance || 0);
  const varPct = Number(d.variance_pct || 0);
  const cpi = Number(d.cpi || 0);
  const cpiClass = cpi >= 1 ? 'green' : cpi >= 0.9 ? 'gold' : 'red';
  const cpiLabel = cpi >= 1 ? 'Tốt' : cpi >= 0.9 ? 'Cảnh báo' : 'Vượt ngân sách';

  document.getElementById('dash-content').innerHTML = `
    <div class="kpi-grid">
      <div class="kpi-tile navy"><div class="kpi-lbl">Tổng HĐ (${d.year})</div><div class="kpi-val">${fmtCompact(tongHD)} <span class="unit">VNĐ</span></div></div>
      <div class="kpi-tile"><div class="kpi-lbl">Ngân sách KH</div><div class="kpi-val">${fmtCompact(budget)} <span class="unit">VNĐ</span></div></div>
      <div class="kpi-tile"><div class="kpi-lbl">Chi phí thực tế</div><div class="kpi-val">${fmtCompact(tongCP)} <span class="unit">VNĐ</span></div></div>
      <div class="kpi-tile ${variance>=0?'green':'red'}"><div class="kpi-lbl">Variance (KH − TT)</div><div class="kpi-val">${fmtCompact(variance)} <span class="unit">VNĐ</span></div><div class="kpi-hint">${varPct}%</div></div>
      <div class="kpi-tile ${cpiClass}"><div class="kpi-lbl">CPI (DT/CP)</div><div class="kpi-val">${cpi.toFixed(2)}</div><div class="kpi-hint">${cpiLabel}</div></div>
    </div>

    <div class="panel"><div class="panel-hd"><h3>Budget vs Actual theo loại CP</h3></div>
      <div style="position:relative;height:320px;width:100%"><canvas id="chart-bva"></canvas></div>
    </div>

    <div class="panel">
      <div class="panel-hd"><h3>Chi tiết BvA</h3></div>
      <div class="table-wrap"><table class="tbl">
        <thead><tr><th>Loại CP</th><th class="r">Ngân sách KH</th><th class="r">Thực tế</th><th class="r">Chênh lệch</th><th class="c">% TT/KH</th><th class="c">Đánh giá</th></tr></thead>
        <tbody id="bva-tbody">${bva.rows.map(r => `
          <tr>
            <td class="small">${r.loai_cp}</td>
            <td class="r money">${formatVND(r.kh)}</td>
            <td class="r money">${formatVND(r.tt)}</td>
            <td class="r money ${r.chenh>=0?'green':'red'}">${formatVND(r.chenh)}</td>
            <td class="c">${r.pct}%</td>
            <td class="c"><span class="dot-badge dot-${r.status==='over'?'red':r.status==='warn'?'orange':'green'}">${r.status==='over'?'Vượt':r.status==='warn'?'Cảnh báo':'OK'}</span></td>
          </tr>`).join('')}</tbody>
        <tfoot><tr class="ttl">
          <td>TỔNG</td>
          <td class="r money">${formatVND(bva.totalKH)}</td>
          <td class="r money">${formatVND(bva.totalTT)}</td>
          <td class="r money">${formatVND(bva.totalKH - bva.totalTT)}</td>
          <td class="c">${bva.totalKH>0?Math.round(bva.totalTT/bva.totalKH*100):0}%</td>
          <td></td>
        </tr></tfoot>
      </table></div>
    </div>

    <div class="panel-grid-2">
      <div class="panel">
        <div class="panel-hd"><h3>Cash Flow tổng quan</h3></div>
        <div class="cf-summary">
          <div class="cf-row"><span>Tổng giá trị HĐ</span><span class="money">${formatVND(tongHD)}</span></div>
          <div class="cf-row"><span class="muted">(−) Doanh thu ghi nhận</span><span class="money green">${formatVND(tongDT)}</span></div>
          <div class="cf-row ttl"><span>Còn lại</span><span class="money">${formatVND(Math.max(0, tongHD - tongDT))}</span></div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-hd"><h3>Chi phí theo loại (top 8)</h3></div>
        <div style="position:relative;height:260px;width:100%"><canvas id="chart-cp-pie"></canvas></div>
      </div>
    </div>
  `;

  renderBvAChart(bva.rows.slice(0, 12));
  renderCpPie(bva.rows.slice(0, 8));
}

function renderBvAChart(rows) {
  if (!rows.length) return;
  renderChart('chart-bva', {
    type: 'bar',
    data: {
      labels: rows.map(r => r.loai_cp.length > 25 ? r.loai_cp.slice(0, 25) + '…' : r.loai_cp),
      datasets: [
        { label: 'Ngân sách KH', data: rows.map(r => r.kh), backgroundColor: PALETTE.navyLight },
        { label: 'Thực tế', data: rows.map(r => r.tt), backgroundColor: PALETTE.orange }
      ]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      scales: { y: { ticks: { callback: v => fmtCompact(v) } } },
      plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': ' + fmtCompact(ctx.raw) + ' VNĐ' } } }
    }
  });
}

function renderCpPie(rows) {
  if (!rows.length) return;
  renderChart('chart-cp-pie', {
    type: 'doughnut',
    data: {
      labels: rows.map(r => r.loai_cp.length > 20 ? r.loai_cp.slice(0, 20) + '…' : r.loai_cp),
      datasets: [{
        data: rows.map(r => r.tt),
        backgroundColor: [PALETTE.navy, PALETTE.navyLight, PALETTE.orange, PALETTE.blue, PALETTE.purple, PALETTE.green, PALETTE.gold, '#a1887f'],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'right' }, tooltip: { callbacks: { label: ctx => ctx.label + ': ' + fmtCompact(ctx.raw) } } }
    }
  });
}
